import { useState, useEffect } from 'react'
import { makeApiRequest } from '../utils/api'

export interface KillSwitchStatus {
  global: {
    active: boolean;
    reason: string | null;
    updated: string;
  };
  files: Array<{
    id: string;
    resource_id: string;
    reason: string | null;
    created_at: string;
  }>;
  folders: Array<{
    id: string;
    resource_id: string;
    reason: string | null;
    created_at: string;
  }>;
}

export interface KillSwitchLog {
  id: string;
  action: string;
  details: string | null;
  created_at: string;
}

// Owns kill-switch status/logs data + toggle/remove mutations. The consuming
// modal keeps its tab + reason input state and presentation. Behaviour mirrors
// the previous in-component logic exactly (fetch on open, refetch after mutate).
export function useKillSwitch(isOpen: boolean, onRefresh?: () => void) {
  const [status, setStatus] = useState<KillSwitchStatus | null>(null);
  const [logs, setLogs] = useState<KillSwitchLog[]>([]);
  const [loading, setLoading] = useState(false);
  const [toggling, setToggling] = useState(false);

  useEffect(() => {
    if (isOpen) {
      fetchStatus();
      fetchLogs();
    }
  }, [isOpen]);

  const fetchStatus = async () => {
    try {
      setLoading(true);
      const data = await makeApiRequest('/api/kill-switch/status');
      setStatus(data);
    } catch (error) {
      console.error('Failed to fetch kill switch status:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchLogs = async () => {
    try {
      const data = await makeApiRequest('/api/kill-switch/logs');
      setLogs(data.logs || []);
    } catch (error) {
      console.error('Failed to fetch logs:', error);
    }
  };

  // Takes the reason from the modal's input; returns true on success so the
  // caller can clear that input field.
  const handleToggleGlobal = async (globalReason: string): Promise<boolean> => {
    if (!status) return false;

    const newState = !status.global.active;

    if (newState && !globalReason.trim()) {
      alert('Please provide a reason for activating the kill switch');
      return false;
    }

    try {
      setToggling(true);
      await makeApiRequest('/api/kill-switch/toggle', {
        method: 'POST',
        body: JSON.stringify({
          type: 'global',
          active: newState,
          reason: newState ? globalReason : null
        })
      });

      await fetchStatus();
      await fetchLogs();

      if (onRefresh) onRefresh();

      alert(`Kill switch ${newState ? 'ACTIVATED' : 'DEACTIVATED'} successfully!`);
      return true;
    } catch (error) {
      console.error('Failed to toggle kill switch:', error);
      alert('Failed to toggle kill switch');
      return false;
    } finally {
      setToggling(false);
    }
  };

  const handleRemoveResource = async (id: string) => {
    if (!confirm('Remove this resource from kill switch?')) return;

    try {
      await makeApiRequest(`/api/kill-switch/resource/${id}`, {
        method: 'DELETE'
      });
      await fetchStatus();
      await fetchLogs();

      if (onRefresh) onRefresh();
    } catch (error) {
      console.error('Failed to remove resource:', error);
      alert('Failed to remove resource');
    }
  };

  return { status, logs, loading, toggling, handleToggleGlobal, handleRemoveResource };
}
